import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Dynamic section numbering (per ADR-0049).
 *
 * A template declares its document sections in fixed order on
 * `metadata.sections`. The rendered § number is the 1-based position of a
 * section among those currently *visible* for the contract's values —
 * never the declaration index — so a conditionally-hidden section leaves
 * no gap in the sequence.
 *
 * The numbering is computed once and shared through React context: the
 * `<Section>` atom reads its own number by `ref`, and the editor reads
 * the whole map to label its panels ("§1–2 Parter") and to scroll the
 * preview to `id="section-<ref>"` on field focus.
 */
import { createContext, useContext, useMemo, } from 'react';
import { evaluateVisibilityRule } from '../visibility.js';
import { customSectionAnchorRef, groupCustomsByAfterRef, readCustomSections, } from '../custom-sections.js';
const SectionNumberingContext = createContext(null);
/**
 * The contract's visible custom sections (per ADR-0060) grouped by the
 * declared `ref` they should render after (`afterRef`). The `<Section>` atom
 * for ref X looks up its slice and emits those custom sections as siblings
 * right after itself; each resolves its own § number from the numbering map
 * via `customSectionAnchorRef`.
 */
const CustomSectionsByAfterRefContext = createContext(new Map());
/** Custom sections anchored to render immediately after the section `ref`. */
export function useCustomSectionsAfter(ref) {
    return useContext(CustomSectionsByAfterRefContext).get(ref) ?? [];
}
/**
 * The contract's visible custom sections in array order (per ADR-0061) —
 * used by `<CustomSectionsBody>` for frame-less templates whose body IS the
 * custom content. Empty unless a `<SectionNumberingProvider>` is present.
 */
const CustomSectionsOrderedContext = createContext([]);
/** The contract's visible custom sections, in order, for frame-less bodies. */
export function useCustomSectionsOrdered() {
    return useContext(CustomSectionsOrderedContext);
}
/** DOM anchor id for a section ref — shared by the atom and the editor. */
export const sectionAnchorId = (ref) => `section-${ref}`;
/**
 * Resolve a section's human title from a template's declared sections.
 * Falls back to the raw ref/string for legacy templates that never
 * declared `metadata.sections`.
 */
export function resolveSectionTitle(sections, ref) {
    return sections?.find((s) => s.ref === ref)?.title ?? ref;
}
/**
 * Compute § numbers for a declared section list. In `mode='blank'` every
 * section is visible (authors see the full possible shape); otherwise a
 * section's `visibleWhen` decides whether it consumes a number.
 *
 * Per-contract custom sections (ADR-0060) read from `values` are spliced in
 * immediately after the declared section named by each custom's resolved
 * `afterRef` (default: the second-to-last section, i.e. before the closing /
 * signing block), under map keys `customSectionAnchorRef(id)`. A custom whose
 * anchor section is hidden is itself hidden (consumes no number) — keeping the
 * numbers in lock-step with the DOM, where the `<Section>` atom only emits its
 * anchored customs when the section itself renders. Sections before an anchor
 * are untouched, so sub-clause path resolution stays correct.
 *
 * Frame-less templates (per ADR-0061) declare no sections — their whole body
 * IS the custom sections. There's no anchor to splice against, so the customs
 * are simply numbered `1..N` in array order and rendered by `<CustomSectionsBody>`.
 */
export function computeSectionNumbers(sections, values, mode, opts) {
    const map = new Map();
    const customs = opts?.includeCustomSections === false ? [] : readCustomSections(values, mode);
    // Frame-less: no declared sections → number the customs directly.
    if (sections.length === 0) {
        let m = 0;
        for (const cs of customs)
            map.set(customSectionAnchorRef(cs.id), (m += 1));
        return map;
    }
    const byAfter = groupCustomsByAfterRef(customs, sections);
    let n = 0;
    for (const section of sections) {
        const visible = mode === 'blank' || evaluateVisibilityRule(section.visibleWhen, values);
        if (!visible) {
            map.set(section.ref, null);
            continue;
        }
        map.set(section.ref, (n += 1));
        for (const cs of byAfter.get(section.ref) ?? []) {
            map.set(customSectionAnchorRef(cs.id), (n += 1));
        }
    }
    return map;
}
/**
 * Provides computed section numbering to descendant `<Section>` atoms.
 * Wrap a template Body in this (passing the module's `metadata.sections`,
 * the current `values`, and `mode`). Legacy templates omit it; `<Section>`
 * then falls back to its `n` prop.
 */
export function SectionNumberingProvider({ sections, values, mode, customSections = true, children, }) {
    const numbering = useMemo(() => computeSectionNumbers(sections, values, mode, {
        includeCustomSections: customSections,
    }), [sections, values, mode, customSections]);
    const ordered = useMemo(() => (customSections ? readCustomSections(values, mode) : []), [values, mode, customSections]);
    const byAfter = useMemo(() => groupCustomsByAfterRef(ordered, sections), [ordered, sections]);
    return (_jsx(SectionNumberingContext.Provider, { value: numbering, children: _jsx(CustomSectionsByAfterRefContext.Provider, { value: byAfter, children: _jsx(CustomSectionsOrderedContext.Provider, { value: ordered, children: children }) }) }));
}
/**
 * Compute sub-clause positions within a Section, mirroring the top-level
 * SectionNumbering compression: a hidden entry gets `null` and consumes
 * no position; the rest compact 1-based ('1'/'2'/… in 'digit' format,
 * 'a'/'b'/… in 'letter' format). Caller composes the full reference,
 * e.g. `${sectionN}.${subs.get('moms')}` → "22.2",
 * or  `${sectionN}.${subs.get('moms')}.${letters.get('tenant')}` → "22.2.a".
 *
 * Per ADR-0049 we support exactly three levels of numbering — section
 * (digit), sub (digit), sub-sub (letter). Deeper nesting belongs in the
 * Word-source clause, not the rendered document.
 */
export function subClauseNumbers(entries, format = 'digit') {
    const map = new Map();
    let n = 0;
    for (const entry of entries) {
        if (!entry.visible) {
            map.set(entry.id, null);
            continue;
        }
        n += 1;
        if (format === 'letter') {
            // 'a' = 97. Cap at 'z' (we never expect anywhere near that deep).
            map.set(entry.id, String.fromCharCode(96 + Math.min(n, 26)));
        }
        else {
            map.set(entry.id, String(n));
        }
    }
    return map;
}
/**
 * Read a section's computed state by ref. Returns `{ number, hidden }`.
 * `number` is null and `hidden` true when the section is gated out for
 * the current values. Returns `{ number: null, hidden: false }` when no
 * provider is present (legacy path) so the caller can fall back to `n`.
 */
export function useSectionNumber(ref) {
    const numbering = useContext(SectionNumberingContext);
    if (!numbering || !numbering.has(ref)) {
        return { number: null, hidden: false };
    }
    const number = numbering.get(ref) ?? null;
    return { number, hidden: number === null };
}
/**
 * Resolve a dotted section path to its full numeric reference, applying
 * the same compress-on-visibility model used by `computeSectionNumbers`
 * and `subClauseNumbers`.
 *
 * - `'skatter-avgifter'` (top-level only) → `'22'`
 * - `'skatter-avgifter.moms'` → `'22.3'`
 * - `'skatter-avgifter.moms.tenant'` → `'22.3.a'`
 *
 * Returns `null` when any segment in the path is hidden for the current
 * values (so the editor can suppress the heading or fall back).
 *
 * Format defaults: depth 1 → digit, depth 2 → letter. A SubClauseDef may
 * override its own format via the `format` field.
 */
export function resolveSectionPath(sections, path, values, mode) {
    const segments = path.split('.').filter(Boolean);
    if (segments.length === 0)
        return null;
    const topNumbering = computeSectionNumbers(sections, values, mode);
    const topNum = topNumbering.get(segments[0]);
    if (topNum == null)
        return null;
    let composed = String(topNum);
    let currentDefs = sections.find((s) => s.ref === segments[0])?.subClauses;
    for (let depth = 1; depth < segments.length; depth++) {
        if (!currentDefs)
            return null;
        const entries = currentDefs.map((d) => ({
            id: d.ref,
            visible: mode === 'blank' || evaluateVisibilityRule(d.visibleWhen, values),
        }));
        // Inherit format from the def; fall back to digit at depth 1, letter at depth 2+.
        const seg = segments[depth];
        const def = currentDefs.find((d) => d.ref === seg);
        const format = def?.format ?? (depth >= 2 ? 'letter' : 'digit');
        const numbering = subClauseNumbers(entries, format);
        const pos = numbering.get(seg);
        if (pos == null)
            return null;
        composed = `${composed}.${pos}`;
        currentDefs = def?.subClauses;
    }
    return composed;
}
// ───────────────────────────────────────────────────────────────────────
// Sub-clause numbering — one level inside a Section (digit), and one more
// level inside a sub-clause (letter). Same compress-on-visibility model as
// the top-level sections.
//
// Use:
//   <Section sectionRef="X" title="…">         // sets path to '22'
//     <SubClauseProvider entries={[…]}>        // computes child positions
//       <SubClause id="moms" title="Mervärdesskatt">
//         <p>plain body…</p>
//         <SubClauseProvider entries={[…]} format="letter">
//           <SubClause id="tenant" title="Hyresgästens momsplikt">…</SubClause>
//           <SubClause id="landlord" title="Hyresvärdens momsplikt">…</SubClause>
//         </SubClauseProvider>
//       </SubClause>
//     </SubClauseProvider>
//   </Section>
//
// Each SubClause looks up its own position from the nearest provider and
// composes the full ref as `${parentPath}.${position}` — Section pushes
// '22' as the initial path; nested SubClause pushes '22.2' before its
// children render; sub-sub becomes '22.2.a'.
// ───────────────────────────────────────────────────────────────────────
/** Full numeric path of the enclosing clause level (e.g. "22" or "22.2"). */
const ClausePathContext = createContext('');
const SubClauseNumberingContext = createContext(null);
/** Returns the full path of the enclosing clause level. */
export function useClausePath() {
    return useContext(ClausePathContext);
}
/**
 * Wraps a group of `<SubClause>` siblings, computing each one's position
 * 1..N (or a..z) from the `entries` list. Hidden entries consume no
 * position and the rest compact. `format` defaults to 'digit'; pass
 * 'letter' for sub-sub-clause groups.
 */
export function SubClauseProvider({ entries, format = 'digit', children, }) {
    const numbering = useMemo(() => subClauseNumbers(entries, format), [entries, format]);
    return (_jsx(SubClauseNumberingContext.Provider, { value: numbering, children: children }));
}
/**
 * A numbered sub-clause inside the nearest enclosing Section (or
 * SubClause for the second level). Reads its position from the nearest
 * `<SubClauseProvider>` and the parent path from `<Section>` /
 * surrounding `<SubClause>`. Renders a heading "<path> Title" and pushes
 * the composed path down so any nested `<SubClause>` children resolve
 * correctly. Returns null when hidden — no manual `cond()` gate needed.
 */
export function SubClause({ id, title, children, }) {
    const parentPath = useContext(ClausePathContext);
    const numbering = useContext(SubClauseNumberingContext);
    const position = numbering?.get(id);
    if (position == null)
        return null;
    const path = parentPath ? `${parentPath}.${position}` : position;
    return (_jsxs(ClausePathContext.Provider, { value: path, children: [_jsxs("p", { className: "mt-3 mb-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.08em] text-stone-600", children: [path, " ", title] }), children] }));
}
/** Internal — Section atom uses this to push its computed number as the
 *  initial ClausePath for any sub-clauses it contains. */
export function ClausePathRoot({ path, children, }) {
    return (_jsx(ClausePathContext.Provider, { value: path, children: children }));
}
//# sourceMappingURL=section-numbering.js.map